import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { PortalApiService } from '../../core/portal-api.service';
import { PortalAuthService } from '../../core/portal-auth.service';
import { PortalStateService } from '../../core/portal-state.service';
import { I18nService } from '../../core/i18n.service';
import { buildMonths, formatMonth } from '../../core/months';

// What the company's employees actually bought. Two views over the same data:
// a per-employee roll-up (the question customers usually ask) and the raw
// transaction list behind it.
@Component({
  selector: 'portal-purchases',
  templateUrl: './purchases.component.html',
  styleUrls: ['./purchases.component.css']
})
export class PurchasesComponent implements OnInit, OnDestroy {

  loading = true;
  error = '';
  month = moment().format('YYYY-MM');
  groupFilter = '';
  search = '';
  view: 'people' | 'transactions' = 'people';
  transactions: any[] = [];

  // Derived state lives in FIELDS, recomputed explicitly by recompute().
  // None of this may become a getter bound to *ngFor: byEmployee builds new
  // row objects, so a getter would hand ngFor 'new' items on every
  // change-detection pass, rebuilding the table endlessly. See core/months.ts.
  readonly months = buildMonths();
  groups: any[] = [];
  filteredTransactions: any[] = [];
  byEmployee: any[] = [];
  totals = { count: 0, total: 0, subsidy: 0 };

  private sub: Subscription;

  constructor(
    private api: PortalApiService,
    private auth: PortalAuthService,
    private state: PortalStateService,
    public i18n: I18nService
  ) { }

  monthOptionLabel(value: string): string { return formatMonth(value, this.i18n.lang); }

  ngOnInit() {
    this.sub = this.state.customerId$.subscribe(() => {
      this.refreshGroups();
      this.load();
    });
  }

  ngOnDestroy() { if (this.sub) { this.sub.unsubscribe(); } }

  private refreshGroups() {
    const s = this.auth.session;
    const scope = this.state.customerId;
    this.groups = ((s && s.groups) || []).filter((g: any) => !scope || g.customerId === scope);
  }

  async load() {
    this.loading = true;
    this.error = '';
    const from = moment(this.month, 'YYYY-MM').startOf('month').unix();
    const to = moment(this.month, 'YYYY-MM').endOf('month').unix();
    try {
      const res: any = await this.api.purchases(from, to, {
        customerId: this.state.customerId || undefined,
        groupDocId: this.groupFilter || undefined
      });
      this.transactions = (res && res.transactions) || [];
      this.recompute();
    } catch (err: any) {
      const body = err && err.error;
      this.error = (body && body.message) || this.i18n.t('purchases.errorLoadFailed');
      this.transactions = [];
      this.recompute();
    }
    this.loading = false;
  }

  onFilterChange() { this.load(); }

  // Called on load and whenever the search box changes — never from a getter.
  recompute() {
    const term = this.search.trim().toLowerCase();
    this.filteredTransactions = !term ? this.transactions : this.transactions.filter(t =>
      (t.employeeName || '').toLowerCase().includes(term) ||
      (t.employeeEmail || '').toLowerCase().includes(term) ||
      (t.productName || '').toLowerCase().includes(term));

    // Roll-up per person, from the same filtered list so the search box
    // narrows both views identically.
    const map: { [key: string]: any } = {};
    this.filteredTransactions.forEach(t => {
      const key = t.employeeEmail || t.employeeName || 'unknown';
      if (!map[key]) {
        map[key] = { name: t.employeeName || key, email: t.employeeEmail || '', count: 0, total: 0, subsidy: 0 };
      }
      map[key].count += 1;
      map[key].total += t.salePriceIncSubsidy || t.price || 0;
      map[key].subsidy += t.subsidizedExVAT || 0;
    });
    this.byEmployee = Object.values(map).sort((a: any, b: any) => b.total - a.total);

    this.totals = this.filteredTransactions.reduce((acc, t) => ({
      count: acc.count + 1,
      total: acc.total + (t.salePriceIncSubsidy || t.price || 0),
      subsidy: acc.subsidy + (t.subsidizedExVAT || 0)
    }), { count: 0, total: 0, subsidy: 0 });
  }

  exportCsv() {
    const rows = [[
      this.i18n.t('purchases.colDate'), this.i18n.t('purchases.colEmployee'), this.i18n.t('purchases.colEmail'),
      this.i18n.t('purchases.colProduct'), this.i18n.t('purchases.colEmployeePaid'),
      this.i18n.t('purchases.csvSubsidyExVat'), this.i18n.t('purchases.colPayment')
    ]];
    this.filteredTransactions.forEach(t => rows.push([
      this.date(t.unix), t.employeeName || '', t.employeeEmail || '', t.productName || '',
      String(t.salePriceIncSubsidy || t.price || 0), String(t.subsidizedExVAT || 0), t.payType || ''
    ]));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `purchases-${this.month}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  date(unix: number): string {
    return unix ? moment.unix(unix).format('DD.MM HH:mm') : '—';
  }

  kr(v: number): string {
    return (Number(v) || 0).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
